// frontend/src/components/ProjectDetails.js
// Componente para mostrar los detalles de un proyecto, sus fases y actividades.
// CORREGIDO: Botón de Sincronizar reactivado.
// ACTUALIZADO: Manejo visual de fases no aplicables en proyectos cerrados.
// NUEVA FUNCIONALIDAD: Muestra el 'Nombre del Proyecto' para iniciativas PoC/Implementación en la cabecera.
// NUEVA FUNCIONALIDAD: Añadida pestaña de "Historial de Actividad" con logs de auditoría.

import React, { useEffect, useState } from 'react';
import { useProjectStore } from '../store/projectStore';
import LoadingSpinner from '../components/LoadingSpinner';
import ProjectFilesAndComments from '../components/ProjectFilesAndComments';
import {
  CheckCircleIcon, ArrowLeftIcon, TagIcon, ArrowPathIcon,
  InformationCircleIcon, MinusCircleIcon, ClockIcon, DocumentTextIcon, ChatBubbleLeftEllipsisIcon
} from '@heroicons/react/24/solid';

function ProjectDetails({ projectId, onBack }) {
  const {
    selectedProject,
    fetchProjectById,
    isLoadingProjects,
    toggleActivityCompletion,
    syncActivities,
    projectAuditLogs, // NUEVO: Obtener logs de auditoría del store
    isLoadingAuditLogs, // NUEVO: Obtener estado de carga de logs
    fetchProjectAuditLogs, // NUEVO: Obtener la acción para cargar logs
  } = useProjectStore();

  const [lastInteractedPhaseId, setLastInteractedPhaseId] = useState(null);
  const [activeTab, setActiveTab] = useState('phases'); // Estado para la pestaña activa: 'phases', 'filesAndComments', 'auditLogs'

  useEffect(() => {
    if (projectId) {
      fetchProjectById(projectId);
    }
  }, [projectId, fetchProjectById]);

  // NUEVO: Cargar logs de auditoría cuando se selecciona el proyecto o cambia la pestaña a 'auditLogs'
  useEffect(() => {
    if (selectedProject && activeTab === 'auditLogs') {
      fetchProjectAuditLogs(selectedProject.id);
    }
  }, [selectedProject, activeTab, fetchProjectAuditLogs]);


  const handleActivityToggle = async (activityId, phaseId) => {
    setLastInteractedPhaseId(phaseId);
    const activity = selectedProject?.phases
      .flatMap(p => p.activities)
      .find(a => a.id === activityId);
    if (activity) {
      await toggleActivityCompletion(activityId, !activity.isCompleted);
    }
  };

  useEffect(() => {
    if (lastInteractedPhaseId) {
      const phaseElement = document.getElementById(`phase-${lastInteractedPhaseId}`);
      if (phaseElement) {
        phaseElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setLastInteractedPhaseId(null);
      }
    }
  }, [selectedProject, lastInteractedPhaseId]);


  if (isLoadingProjects || !selectedProject) {
    return (
      <div className="flex justify-center items-center h-screen-content">
        <LoadingSpinner size="lg" />
        <p className="ml-4 text-gray-700">Cargando detalles de la iniciativa...</p>
      </div>
    );
  }

  const projectPhases = selectedProject.phases || [];
  const isMapProject = selectedProject.initiativeType?.name === 'MAP';

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  // NUEVO: Componente para renderizar los logs de auditoría
  const renderAuditLogs = () => {
    if (isLoadingAuditLogs) {
      return (
        <div className="flex justify-center items-center h-48">
          <LoadingSpinner size="lg" />
          <p className="ml-4 text-gray-700">Cargando historial de actividad...</p>
        </div>
      );
    }

    if (projectAuditLogs.length === 0) {
      return (
        <div className="text-center py-10 px-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <InformationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay historial de actividad</h3>
          <p className="mt-1 text-sm text-gray-500">Este proyecto aún no tiene registros de actividad.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {projectAuditLogs.map(log => (
          <div key={log.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm font-semibold text-gray-800">{log.description}</p>
            <p className="text-xs text-gray-500 mt-1">
              Por: <span className="font-medium text-gray-700">{log.user?.username || 'Usuario Desconocido'}</span>
              {' el '}
              <span className="font-medium text-gray-700">{formatDate(log.timestamp)}</span>
            </p>
            {log.oldValue !== null && log.newValue !== null && (
              <div className="mt-2 text-xs text-gray-600">
                <p><strong>Valor Anterior:</strong> {JSON.stringify(log.oldValue)}</p>
                <p><strong>Nuevo Valor:</strong> {JSON.stringify(log.newValue)}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-xl w-full max-w-6xl mx-auto my-8">
      <div className="flex justify-between items-center mb-6 border-b pb-4">
        <div>
          <span className="inline-block bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full mb-2">
            {selectedProject.initiativeType?.name || 'Iniciativa'}
          </span>
          <h2 className="text-3xl font-extrabold text-gray-900">
            Detalles de la Iniciativa:
            {!isMapProject && selectedProject.projectName ? ( // NUEVO: Muestra el nombre del proyecto si no es MAP
                <span className="ml-2"> {selectedProject.projectName} - </span>
            ) : (
                <span className="ml-2"> {selectedProject.client?.organizationName}{selectedProject.client?.opportunityNumber ? ` (${selectedProject.client?.opportunityNumber})` : ''}</span> // Si es MAP o no hay project name, muestra lo original
            )}
            {/* Si es un proyecto no MAP y tiene nombre, el nombre del cliente/oportunidad va después del nombre del proyecto */}
            {!isMapProject && selectedProject.projectName && selectedProject.client ? (
                <span className="text-xl font-normal text-gray-700"> {selectedProject.client?.organizationName}{selectedProject.client?.opportunityNumber ? ` (${selectedProject.client?.opportunityNumber})` : ''}</span>
            ) : null}
          </h2>
        </div>
        <div className="flex items-center space-x-2">
            <button
                onClick={() => syncActivities(projectId)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center space-x-2 transition-colors"
                title="Sincronizar con las actividades base de la plantilla"
            >
                <ArrowPathIcon className="h-5 w-5" />
                <span>Sincronizar</span>
            </button>
            <button
                onClick={onBack}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 flex items-center space-x-2 transition-colors"
            >
                <ArrowLeftIcon className="h-5 w-5" />
                <span>Volver</span>
            </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 bg-blue-50 p-6 rounded-lg border border-blue-200">
        <div>
          <p className="text-sm font-medium text-blue-700">Monto Esperado</p>
          <p className="text-2xl font-bold text-blue-900">${selectedProject.totalExpectedAmount ? parseFloat(selectedProject.totalExpectedAmount).toFixed(2) : '0.00'}</p>
        </div>
        <div>
          <p className="text-sm font-medium text-blue-700">Monto Realizado</p>
          <p className="text-2xl font-bold text-blue-900">${selectedProject.realizedAmount ? parseFloat(selectedProject.realizedAmount).toFixed(2) : '0.00'}</p>
        </div>
        <div>
          <p className="text-sm font-medium text-blue-700">Progreso General</p>
          <div className="flex items-center mt-2">
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full"
                style={{ width: `${selectedProject.overallProgress}%` }}
              ></div>
            </div>
            <span className="ml-3 text-lg font-bold text-blue-900">{selectedProject.overallProgress}%</span>
          </div>
        </div>
      </div>

      <div className="mb-8 border-b pb-4">
        <h3 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center space-x-2">
            <TagIcon className="h-6 w-6" />
            <span>Detalles del Cliente</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-gray-700">
            <p><strong>Organización:</strong> {selectedProject.client?.organizationName}</p>
            <p><strong>Nº Oportunidad:</strong> {selectedProject.client?.opportunityNumber || 'Sin número'}</p>
            <p><strong>Account Manager:</strong> {selectedProject.client?.accountManager}</p>
            <p><strong>Solution Sales:</strong> {selectedProject.client?.solutionSales}</p>
        </div>
      </div>

      {/* NUEVO: Pestañas para Fases, Documentos/Comentarios y Historial de Actividad */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('phases')}
            className={`${
              activeTab === 'phases'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <DocumentTextIcon className="h-5 w-5 mr-2" />Fases de la Iniciativa
          </button>
          <button
            onClick={() => setActiveTab('filesAndComments')}
            className={`${
              activeTab === 'filesAndComments'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <ChatBubbleLeftEllipsisIcon className="h-5 w-5 mr-2" />Documentos y Comentarios
          </button>
          <button
            onClick={() => setActiveTab('auditLogs')}
            className={`${
              activeTab === 'auditLogs'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <ClockIcon className="h-5 w-5 mr-2" />Historial de Actividad
          </button>
        </nav>
      </div>

      {/* Contenido de las pestañas */}
      <div>
        {activeTab === 'phases' && (
          <div className="space-y-8">
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Fases de la Iniciativa</h3>
            {projectPhases.length > 0 ? (
              projectPhases.map((phase) => (
                <div key={phase.id} id={`phase-${phase.id}`} className={`p-6 rounded-lg border shadow-sm
                    ${phase.isApplicable ? 'bg-gray-50 border-gray-200' : 'bg-yellow-50 border-yellow-200'}
                `}>
                  <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-200">
                    <h4 className="text-xl font-bold text-gray-900 flex items-center">
                      {phase.name}
                      {selectedProject.status === 'closed' && !phase.isApplicable && (
                        <span className="ml-3 text-base font-normal text-yellow-800 flex items-center">
                          <MinusCircleIcon className="h-5 w-5 mr-1"/> FASE NO REALIZADA
                        </span>
                      )}
                      {phase.expectedAmount && (
                        <span className="ml-3 text-base font-normal text-gray-600">
                          (${parseFloat(phase.expectedAmount).toFixed(2)})
                        </span>
                      )}
                    </h4>
                    <div className="flex items-center">
                      <div className="w-32 bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-green-600 h-3 rounded-full"
                          style={{ width: `${phase.progress}%` }}
                        ></div>
                      </div>
                      <span className="ml-3 text-md font-semibold text-gray-700">{phase.progress}% Completado</span>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {phase.activities.length === 0 ? (
                      <li className="text-gray-600 italic">No hay actividades definidas para esta fase.</li>
                    ) : (
                      phase.activities.map((activity) => (
                        <li key={activity.id} className="flex items-center text-gray-800">
                          <input
                            type="checkbox"
                            checked={activity.isCompleted}
                            onChange={() => handleActivityToggle(activity.id, phase.id)}
                            className="h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            disabled={selectedProject.status === 'closed' && !phase.isApplicable}
                          />
                          <span className={`ml-3 text-base ${activity.isCompleted ? 'line-through text-gray-500' : ''}`}>
                            {activity.description}
                          </span>
                          {activity.isCompleted && (
                            <span className="ml-2 text-green-600">
                              <CheckCircleIcon className="h-5 w-5" />
                            </span>
                          )}
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              ))
            ) : (
              <div className="text-center py-10 px-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <InformationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Sin Fases Definidas</h3>
                <p className="mt-1 text-sm text-gray-500">Este proyecto no tiene fases ni actividades asignadas todavía.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'filesAndComments' && <ProjectFilesAndComments />}
        {activeTab === 'auditLogs' && renderAuditLogs()}
      </div>
    </div>
  );
}

export default ProjectDetails;
